import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { FocusTrapFactory } from '@angular/cdk/a11y';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  AccessibleOverlayHandle,
  makeOverlayAccessible
} from '../overlay/accessible-overlay';
import {
  ComponentRef,
  computed,
  Injectable,
  Injector,
  signal,
  inject
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ConnectOutdatedDialogComponent } from '@cpd-app/core-module/components/connect-oudated-dialog/connect-outdated-dialog.component';
import type { ConnectSidebarOverlayComponent } from '@cpd-app/core-module/components/connect-sidebar/connect-sidebar-overlay.component';
import { combineLatest, noop, Observable, Subject } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { EmployerConnectDialogComponent } from '@cpd-app/core-module/components/employer-connect-dialog/employer-connect-dialog.component';
import {
  OrganizationModel,
  PremiumPartnerConnectionInput,
  PremiumPartnerModel
} from '@cpd-app/core-module/models';
import { GoalFacade } from '@cpd-app/state/goal';
import { UserProfileFacade } from '@cpd-app/state/user-profile';
import { PremiumPartnerModalComponent } from '@cpd-app/core-module/components/premium-partner-modal/premium-partner-modal.component';

@Injectable({
  providedIn: 'root'
})
export class ConnectSidebarService {
  private readonly isSidebarOpen$ = signal<boolean>(false);
  private readonly isOpenOnce$ = signal<boolean>(false);
  private toggleTooltip$ = new Subject<void>();
  private overlayRef!: OverlayRef;
  // Accessibility behavior for the raw CDK overlay; released on detach.
  private a11y: AccessibleOverlayHandle | null = null;
  private portal!: ComponentPortal<ConnectSidebarOverlayComponent>;
  private componentRef!: ComponentRef<ConnectSidebarOverlayComponent>;

  public readonly isSidebarOpen = computed(() => this.isSidebarOpen$());
  public readonly isSidebarOpenOnce = computed(() => this.isOpenOnce$());
  public readonly triggerTooltip$ = this.toggleTooltip$.asObservable();
  public readonly isConnectItemDisabled$ = combineLatest([
    this.goalFacade.isCareerGoalSet$,
    this.userProfileFacade.isSkillsSurveyComplete$
  ]).pipe(
    map(
      ([isCareerGoalSet, isSkillSurveyComplete]) =>
        !isCareerGoalSet || !isSkillSurveyComplete
    )
  );

  public constructor(
    private readonly dialog: MatDialog,
    private readonly goalFacade: GoalFacade,
    private readonly userProfileFacade: UserProfileFacade,
    private overlay: Overlay,
    private injector: Injector,
    private readonly focusTrapFactory: FocusTrapFactory
  ) {}

  public triggerTooltip(): void {
    this.toggleTooltip$.next();
  }

  /**
   * Schools the member ticked in the Request info column, waiting to be
   * reviewed. Held here rather than passed as an argument because the panel is
   * attached through a portal: the service is the only thing both sides can
   * see.
   *
   * It is a REVIEW step, not a queue that sends itself. Asking a school to
   * get in touch hands them the member's profile, which is an outward-facing
   * act, and the design's own control says "Review & send" for that reason.
   */
  public readonly pendingSchools = signal<
    { id: number; name: string }[]
  >([]);

  /**
   * Opens the panel with the ticked schools in it. Named apart from
   * `openConnectSidebar` because that one toggles: a member who has just
   * pressed Review & send is asking for it open, not for it to close because
   * it happened to be open already.
   */
  public async reviewSchools(
    schools: { id: number; name: string }[]
  ): Promise<void> {
    this.pendingSchools.set(schools);

    if (this.isSidebarOpen()) {
      return;
    }

    await this.openConnectSidebar();
  }

  /**
   * How many schools are waiting to be sent to. Shown on the Connect rail, so
   * ticking a school somewhere else on the page has a visible consequence: the
   * member can see where the request went and that it has not gone out yet.
   */
  public readonly pendingCount = computed(() => this.pendingSchools().length);

  /**
   * CONNECT.1. Which section of the Connect panel to bring into view when it
   * opens, for the entry points that point at one (the goals report's "Tell
   * them I am interested", the card after a school request). The panel reads
   * it once and clears it.
   */
  public readonly focusSection = signal<'academic' | 'employer' | null>(null);

  /** CONNECT.1. Opens the panel on one section. Never toggles it shut. */
  public async openConnectAt(section: 'academic' | 'employer'): Promise<void> {
    this.focusSection.set(section);
    if (this.isSidebarOpen()) {
      return;
    }
    await this.openConnectSidebar();
  }

  /** Called once a send has gone out, so a reopen is not pre-filled again. */
  public clearPendingSchools(): void {
    this.pendingSchools.set([]);
  }

  public async openConnectSidebar(): Promise<void> {
    if (!this.overlayRef) {
      await this.createOverlay();
    }

    if (this.overlayRef.hasAttached()) {
      this.closeConnectSidebar();
      return;
    }

    this.isSidebarOpen$.set(true);
    this.isOpenOnce$.set(true);
    this.componentRef = this.overlayRef.attach(this.portal);
    this.a11y = makeOverlayAccessible(this.overlayRef, this.focusTrapFactory, {
      ariaLabel: 'Connect with organizations',
      onEscape: () => this.closeConnectSidebar()
    });
    this.componentRef.instance.closedSidebar.pipe(debounceTime(500)).subscribe({
      next: () => this.closeConnectSidebar(),
      error: noop
    });
  }

  public openOutdatedConnectSidebar(): Observable<{
    acceptNewConnect: boolean;
    acceptPrevConnect: boolean;
  }> {
    return this.dialog
      .open(ConnectOutdatedDialogComponent, {
        ariaLabel: 'Outdated connection',
        width: '420px'
      })
      .afterClosed();
  }

  public openPremiumPartnerModal(
    partner: PremiumPartnerModel,
    disableSkip: boolean
  ): Observable<PremiumPartnerConnectionInput | null> {
    return this.dialog
      .open(PremiumPartnerModalComponent, {
        ariaLabel: 'Partner details',
        data: { partner, disableSkip }
      })
      .afterClosed();
  }

  public openConnectSponsorBanner(
    employerDetail: OrganizationModel
  ): Observable<boolean> {
    return this.dialog
      .open(EmployerConnectDialogComponent, {
        ariaLabel: 'Employer details',
        height: '750px',
        data: { employerDetail },
        position: {
          right: '0'
        },
        panelClass: 'right-slide-dialog'
      })
      .afterClosed();
  }

  public closeConnectSidebar(): void {
    this.isSidebarOpen$.set(false);
    this.a11y?.release();
    this.a11y = null;
    this.overlayRef.detach();
  }

  private async createOverlay(): Promise<void> {
    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      panelClass: 'left-sidebar-panel',
      backdropClass: 'cdk-overlay-blur-backdrop',
      positionStrategy: this.overlay.position().global().left('0').top('0')
    });

    this.overlayRef
      .backdropClick()
      .subscribe({ next: () => this.closeConnectSidebar(), error: noop });

    const { ConnectSidebarOverlayComponent } = await import(
      '@cpd-app/core-module/components/connect-sidebar/connect-sidebar-overlay.component'
    );
    this.portal = new ComponentPortal(
      ConnectSidebarOverlayComponent,
      null,
      this.injector
    );
  }
}
